/**
 * /admin/shorts — Modération des propositions Shorts.
 *
 * Onglets : En attente / Publiés / Refusés.
 * Actions : Publier, Refuser (avec motif), Retirer, Voir signalements.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Clock, Eye, Flag, Heart, Loader2, RefreshCw, Shield, ThumbsUp, Trash2, TrendingUp, Trophy, X } from 'lucide-react';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import useSeo from '../../hooks/useSeo';

const STATUS_LABEL = { pending: 'En attente', published: 'Publié', rejected: 'Refusé' };

export default function AdminShortsPage() {
  const [tab, setTab] = useState('pending');
  const [docs, setDocs] = useState({ pending: [], published: [], rejected: [] });
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [rejectDialog, setRejectDialog] = useState(null); // short à refuser
  const [rejectReason, setRejectReason] = useState('');
  const [reportsDialog, setReportsDialog] = useState(null); // short dont on affiche les signalements
  const [reports, setReports] = useState([]);

  useSeo({ title: 'Modération Shorts — Admin Code 2 Roues', robots: 'noindex, nofollow' });

  const loadStats = useCallback(async () => {
    try {
      const s = await api.adminShortsStats();
      setStats(s);
    } catch (_) { /* silent */ }
  }, []);

  const load = useCallback(async (status = tab) => {
    setLoading(true);
    try {
      const list = await api.adminShortsList(status);
      setDocs((d) => ({ ...d, [status]: list || [] }));
    } catch (e) {
      toast.error(e.message || 'Chargement impossible');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(tab); loadStats(); /* eslint-disable-next-line */ }, [tab]);

  const publishShort = async (id) => {
    try {
      await api.adminShortsPublish(id);
      toast.success('Short publié');
      load('pending'); load('published');
    } catch (e) { toast.error(e.message || 'Échec'); }
  };

  const rejectShort = async () => {
    if (!rejectDialog) return;
    try {
      await api.adminShortsReject(rejectDialog.id, rejectReason);
      toast.success('Short refusé');
      setRejectDialog(null); setRejectReason('');
      load('pending'); load('rejected');
    } catch (e) { toast.error(e.message || 'Échec'); }
  };

  const retireShort = async (id) => {
    if (!window.confirm('Retirer définitivement ce short du site ?')) return;
    try {
      await api.adminShortsRetire(id, 'Retiré par la modération');
      toast.success('Short retiré');
      load('published'); load('rejected');
    } catch (e) { toast.error(e.message || 'Échec'); }
  };

  const openReports = async (short) => {
    setReportsDialog(short);
    try {
      const r = await api.adminShortsReports(short.id);
      setReports(r || []);
    } catch (e) { setReports([]); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="admin-shorts-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-extrabold flex items-center gap-2">
            <Shield className="w-6 h-6 text-[hsl(var(--primary))]" /> Modération Shorts
          </h1>
          <p className="text-sm text-foreground/60 mt-1">Validez, refusez ou retirez les contributions membres.</p>
        </div>
        <Button variant="outline" onClick={() => load(tab)} data-testid="admin-shorts-refresh">
          <RefreshCw className="w-4 h-4 mr-2" /> Rafraîchir
        </Button>
      </div>

      {/* ============ STATS DASHBOARD (3 cartes) ============ */}
      {stats && (
        <section className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6" data-testid="admin-shorts-stats">
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-500/20 text-amber-500 flex items-center justify-center">
              <Clock className="w-6 h-6" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-amber-700 dark:text-amber-300 font-bold">En attente</div>
              <div className="text-3xl font-black">{stats.counts.pending}</div>
              <div className="text-[11px] text-foreground/60">Publiés : {stats.counts.published} · Refusés : {stats.counts.rejected}</div>
            </div>
          </div>
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/20 text-emerald-600 flex items-center justify-center">
              <Eye className="w-6 h-6" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-emerald-700 dark:text-emerald-300 font-bold">Vues totales</div>
              <div className="text-3xl font-black">{(stats.views_total || 0).toLocaleString('fr-FR')}</div>
              <div className="text-[11px] text-foreground/60">{stats.published_count} shorts publiés</div>
            </div>
          </div>
          <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-rose-500/20 text-rose-500 flex items-center justify-center">
              <Heart className="w-6 h-6" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-rose-700 dark:text-rose-300 font-bold">Likes cumulés</div>
              <div className="text-3xl font-black">{(stats.likes_total || 0).toLocaleString('fr-FR')}</div>
              <div className="text-[11px] text-foreground/60">Toutes contributions confondues</div>
            </div>
          </div>
        </section>
      )}

      {/* ============ TOP AUTEURS + TOP THÈMES ============ */}
      {stats && (stats.top_authors.length > 0 || stats.top_themes.length > 0) && (
        <section className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
          {stats.top_authors.length > 0 && (
            <div className="rounded-xl border border-border bg-card p-4" data-testid="admin-shorts-top-authors">
              <div className="flex items-center gap-2 mb-3">
                <Trophy className="w-4 h-4 text-amber-500" />
                <h3 className="text-[12px] font-black uppercase tracking-wider">Top auteurs communauté</h3>
              </div>
              <ol className="space-y-1.5">
                {stats.top_authors.map((a, i) => (
                  <li key={a.name + i} className="flex items-center justify-between text-[13px]">
                    <span className="flex items-center gap-2 min-w-0">
                      <span className="w-5 h-5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-300 text-[10px] font-black flex items-center justify-center">{i + 1}</span>
                      <span className="truncate">{a.name}</span>
                    </span>
                    <span className="text-foreground/60 text-[11.5px] whitespace-nowrap">
                      <Eye className="w-3 h-3 inline mr-0.5" />{a.views} · {a.shorts_count} short{a.shorts_count > 1 ? 's' : ''}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          )}
          {stats.top_themes.length > 0 && (
            <div className="rounded-xl border border-border bg-card p-4" data-testid="admin-shorts-top-themes">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-4 h-4 text-emerald-500" />
                <h3 className="text-[12px] font-black uppercase tracking-wider">Top thèmes (par vues)</h3>
              </div>
              <ol className="space-y-1.5">
                {stats.top_themes.map((t, i) => (
                  <li key={t.theme + i} className="flex items-center justify-between text-[13px]">
                    <span className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 text-[10px] font-black flex items-center justify-center">{i + 1}</span>
                      <span className="capitalize">{t.theme.replace(/-/g, ' ')}</span>
                    </span>
                    <span className="text-foreground/60 text-[11.5px] whitespace-nowrap">
                      <Eye className="w-3 h-3 inline mr-0.5" />{t.views} · {t.shorts_count} shorts
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </section>
      )}


      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="pending" data-testid="tab-pending">
            En attente {docs.pending.length > 0 && <span className="ml-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[hsl(var(--primary))] text-white text-[10px] font-bold">{docs.pending.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="published" data-testid="tab-published">Publiés</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected">Refusés</TabsTrigger>
        </TabsList>

        {['pending', 'published', 'rejected'].map((status) => (
          <TabsContent key={status} value={status} className="mt-6">
            {loading && status === tab ? (
              <div className="text-center py-10 text-sm text-foreground/60">
                <Loader2 className="w-5 h-5 animate-spin mx-auto" /> Chargement…
              </div>
            ) : docs[status].length === 0 ? (
              <Card><CardContent className="py-10 text-center text-sm text-foreground/60">
                Aucun short avec le statut « {STATUS_LABEL[status]} ».
              </CardContent></Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {docs[status].map((s) => (
                  <ShortModerationCard
                    key={s.id}
                    short={s}
                    status={status}
                    onPublish={publishShort}
                    onReject={(sh) => setRejectDialog(sh)}
                    onRetire={retireShort}
                    onOpenReports={openReports}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Dialog refus */}
      <Dialog open={!!rejectDialog} onOpenChange={(open) => !open && setRejectDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Refuser ce short</DialogTitle>
            <DialogDescription>
              Donnez un motif court — il sera visible par l'auteur dans son espace « Mes shorts envoyés ».
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Ex. : contenu valorisant une infraction, droits d'auteur douteux…"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            maxLength={300}
            rows={3}
            data-testid="admin-shorts-reject-reason"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejectDialog(null); setRejectReason(''); }}>Annuler</Button>
            <Button onClick={rejectShort} data-testid="admin-shorts-reject-confirm">Refuser</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog signalements */}
      <Dialog open={!!reportsDialog} onOpenChange={(open) => !open && setReportsDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Signalements — {reportsDialog?.title}</DialogTitle>
            <DialogDescription>{reports.length} signalement(s) reçus.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {reports.length === 0 && <p className="text-sm text-foreground/55">Aucun signalement en base.</p>}
            {reports.map((r) => (
              <div key={r.id} className="rounded-md border border-border/50 p-3">
                <div className="text-xs font-bold uppercase text-red-500">{r.reason}</div>
                {r.note && <p className="text-xs text-foreground/70 mt-1">{r.note}</p>}
                <p className="text-[10px] text-foreground/40 mt-1">
                  {new Date(r.created_at).toLocaleString('fr-FR')}
                </p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ShortModerationCard({ short, status, onPublish, onReject, onRetire, onOpenReports }) {
  return (
    <Card data-testid={`admin-shorts-card-${short.id}`}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-base truncate">{short.title}</CardTitle>
            <p className="text-xs text-foreground/55 mt-0.5">
              Thème : {short.theme} · Source : {short.source_type} · Auteur : {short.author_name || '—'}
            </p>
          </div>
          {short.reports_count > 0 && (
            <button
              type="button"
              onClick={() => onOpenReports(short)}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-100 text-red-700 text-[10px] font-bold hover:bg-red-200"
              data-testid={`admin-shorts-reports-${short.id}`}
            >
              <Flag className="w-3 h-3" /> {short.reports_count}
            </button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {/* Prévisualisation */}
        <div className="relative bg-black rounded-md overflow-hidden mb-3" style={{ aspectRatio: '9 / 16', maxHeight: 260 }}>
          {short.embed_url && !short.video_url ? (
            <iframe
              className="absolute inset-0 w-full h-full"
              src={short.embed_url}
              title={short.title}
              frameBorder="0"
              allow="encrypted-media; picture-in-picture; web-share"
            />
          ) : short.video_url ? (
            <video className="absolute inset-0 w-full h-full object-cover" src={short.video_url} controls muted poster={short.poster_url} />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-white/50 text-xs">Pas de prévisualisation</div>
          )}
        </div>
        {short.description && (
          <p className="text-sm text-foreground/80 mb-3">{short.description}</p>
        )}
        {/* ======== Feedback moniteur (si existant) ======== */}
        {(short.teacher_recommendation || short.teacher_comment) && (
          <div className="rounded-lg border border-indigo-500/30 bg-indigo-500/5 p-2.5 mb-3 text-[12.5px]">
            <div className="flex items-center gap-1.5 mb-1 flex-wrap">
              <span className="text-[10px] uppercase tracking-wider font-black text-indigo-600 dark:text-indigo-400 inline-flex items-center gap-1">
                <ThumbsUp className="w-3 h-3" /> Avis moniteur
              </span>
              {short.teacher_recommendation === 'recommend' && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 text-[9.5px] font-black uppercase tracking-wider border border-emerald-500/30">
                  Recommandé
                </span>
              )}
              {short.teacher_recommendation === 'warn' && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-300 text-[9.5px] font-black uppercase tracking-wider border border-amber-500/30">
                  À vérifier
                </span>
              )}
              {short.teacher_feedback_by && (
                <span className="text-[10px] text-foreground/60">· {short.teacher_feedback_by}</span>
              )}
            </div>
            {short.teacher_comment && (
              <p className="text-foreground/80 leading-snug">{short.teacher_comment}</p>
            )}
          </div>
        )}
        {short.reject_reason && (
          <div className="text-xs text-red-500 mb-2">Motif de refus : {short.reject_reason}</div>
        )}
        <div className="flex gap-2 flex-wrap">
          {status !== 'published' && (
            <Button size="sm" onClick={() => onPublish(short.id)} data-testid={`publish-${short.id}`}>
              <CheckCircle2 className="w-4 h-4 mr-1" /> Publier
            </Button>
          )}
          {status === 'pending' && (
            <Button size="sm" variant="outline" onClick={() => onReject(short)} data-testid={`reject-${short.id}`}>
              <X className="w-4 h-4 mr-1" /> Refuser
            </Button>
          )}
          {status === 'published' && (
            <Button size="sm" variant="destructive" onClick={() => onRetire(short.id)} data-testid={`retire-${short.id}`}>
              <Trash2 className="w-4 h-4 mr-1" /> Retirer
            </Button>
          )}
          <Link
            to={`/shorts?s=${encodeURIComponent(short.id)}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center text-xs text-foreground/60 underline"
          >
            Voir dans le fil
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}
